import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { ArrowRight, LockKeyhole, MapPin, PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { useQuoteBuilderApi } from '../api/client'
import {
  applyQuoteChange,
  applyWorkflowChange,
  quoteQueryOptions,
  workflowQueryOptions,
} from '../api/queries'
import type { Quote, QuoteWorkflow, WorkflowStage } from '../api/types'
import { unfinishedImportsQueryOptions } from '../imports/api'
import { ErrorState, LoadingState } from '../ui/page'
import { StageMark } from '../ui/status'
import { stageStatusLabels } from '../ui/status-labels'
import { StartedProject } from '../project/started-project'
import { workflowTabs, workflowTabsByKey } from './tab-registry'
import type { WorkflowStageKey } from './types'

function StageLink({
  quoteId,
  stage,
  active,
  collapsed,
}: {
  quoteId: string
  stage: WorkflowStage
  active: boolean
  collapsed: boolean
}) {
  return (
    <Link
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex min-h-10 items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium outline-none transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/50',
        collapsed && 'justify-center px-2',
        active ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
      )}
      params={{ quoteId, stage: stage.key }}
      title={collapsed ? stage.label : undefined}
      to="/quotes/$quoteId/workflow/$stage"
    >
      <StageMark status={stage.status} />
      <span className={cn('min-w-0 flex-1 truncate', collapsed && 'sr-only')}>{stage.label}</span>
      <span className="sr-only">{stageStatusLabels[stage.status]}</span>
    </Link>
  )
}

function UnavailableStage({ stage }: { stage: WorkflowStage }) {
  return (
    <section className="grid min-h-64 place-items-center rounded-xl border bg-card px-5 text-center">
      <div>
        <LockKeyhole aria-hidden="true" className="mx-auto size-6 text-muted-foreground" />
        <h2 className="mt-3 text-sm font-semibold">{stage.label} is not ready</h2>
        <p className="mt-1 text-sm text-muted-foreground">{stage.detail}</p>
      </div>
    </section>
  )
}

/**
 * What the open stage still needs from the customer (USA-1140): a quote can start with only a
 * project name, so a stage names the missing bill-to or ship-to and links straight to it.
 */
function MissingInfo({ quoteId, stage }: { quoteId: string; stage: WorkflowStage }) {
  if (!stage.missing?.length) return null
  return (
    <div
      aria-label={`${stage.label} needs`}
      className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
      role="group"
    >
      <MapPin aria-hidden="true" className="size-4 shrink-0" />
      {stage.missing.map((item) => (
        <Link
          className="inline-flex min-h-8 items-center gap-1 font-medium underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
          key={item.field}
          params={{ quoteId, stage: 'customer' }}
          search={{ focus: item.field }}
          to="/quotes/$quoteId/workflow/$stage"
        >
          {item.label}
          <ArrowRight aria-hidden="true" className="size-3.5" />
        </Link>
      ))}
    </div>
  )
}

/**
 * The staged quote workspace (demo `quote-workflow-shell.tsx`): a stage rail with status marks on
 * desktop, a stage picker on smaller screens, and every visited stage kept mounted so switching
 * back is instant and unsaved edits survive. Remaining read-only stages warm in the background.
 */
export function QuoteWorkflowShell({ quoteId, stageKey }: { quoteId: string; stageKey: string }) {
  return <WorkflowShell key={quoteId} quoteId={quoteId} stageKey={stageKey} />
}

function WorkflowShell({ quoteId, stageKey }: { quoteId: string; stageKey: string }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const api = useQuoteBuilderApi()
  const quoteQuery = useQuery(quoteQueryOptions(api, quoteId))
  const workflowQuery = useQuery(workflowQueryOptions(api, quoteId))
  const activeTab = workflowTabsByKey.get(stageKey as WorkflowStageKey)
  const [visited, setVisited] = useState<WorkflowStageKey[]>([])
  const [navCollapsed, setNavCollapsed] = useState(false)
  const loaded = Boolean(quoteQuery.data && workflowQuery.data)
  const workflowState = workflowQuery.data?.state

  // Equipment lists unfinished imports above the worksheet; reading them with the quote keeps the
  // worksheet from jumping down when they arrive (USA-1337).
  useEffect(() => {
    void queryClient.prefetchQuery(unfinishedImportsQueryOptions(api, quoteId))
  }, [api, queryClient, quoteId])

  useEffect(() => {
    if (!loaded || workflowState === 'started') return
    let cancelled = false
    let warming = false
    const warm = async () => {
      // Let the open stage finish loading before warming the remaining read-only stages.
      if (cancelled || warming || queryClient.isFetching()) return
      warming = true
      for (const tab of workflowTabs) {
        if (cancelled) return
        if (!tab.warm) continue
        await queryClient.ensureQueryData(tab.warm(api, quoteId)).catch(() => undefined)
      }
    }
    let timer: number | undefined
    const schedule = () => {
      window.clearTimeout(timer)
      timer = window.setTimeout(() => void warm(), 0)
    }
    const unsubscribe = queryClient.getQueryCache().subscribe(schedule)
    schedule()
    return () => {
      cancelled = true
      unsubscribe()
      window.clearTimeout(timer)
    }
  }, [api, quoteId, loaded, workflowState, stageKey, queryClient])

  // Keep every visited stage mounted (hidden) so returning to it is instant and keeps its edits.
  if (activeTab && !visited.includes(activeTab.key)) setVisited([...visited, activeTab.key])

  if (quoteQuery.isLoading || workflowQuery.isLoading) return <LoadingState label="Loading quote" />
  if (!quoteQuery.data || !workflowQuery.data) {
    return (
      <ErrorState
        description="The quote could not be loaded."
        onRetry={() => {
          void quoteQuery.refetch()
          void workflowQuery.refetch()
        }}
        title="Quote unavailable"
      />
    )
  }
  if (!activeTab) return <ErrorState description="Choose a stage from the quote workflow." title="Stage not found" />

  const quote = quoteQuery.data
  const workflow = workflowQuery.data
  const activeStage = workflow.stages.find((stage) => stage.key === activeTab.key)
  const onQuoteChange = (next: Quote) => applyQuoteChange(queryClient, quoteId, next)
  const onWorkflowChange = (next: QuoteWorkflow) => applyWorkflowChange(queryClient, quoteId, next)

  return (
    <div className="mx-auto w-full max-w-[1640px]" data-workflow-shell="mounted">
      {quoteQuery.isError || workflowQuery.isError ? (
        <p className="mb-3 text-sm text-destructive" role="alert">
          Latest updates could not be loaded. Your edits are still here.{' '}
          <button
            className="font-medium underline underline-offset-2"
            onClick={() => {
              void quoteQuery.refetch()
              void workflowQuery.refetch()
            }}
            type="button"
          >
            Retry
          </button>
        </p>
      ) : null}

      <div className="mb-3 lg:hidden">
        <Select
          onValueChange={(stage) => void navigate({ to: '/quotes/$quoteId/workflow/$stage', params: { quoteId, stage } })}
          value={activeTab.key}
        >
          <SelectTrigger aria-label="Quote stage" className="h-11 w-full bg-card">
            <SelectValue />
          </SelectTrigger>
          <SelectContent position="popper">
            {workflow.stages.map((stage) => (
              <SelectItem key={stage.key} value={stage.key}>
                <StageMark status={stage.status} />
                <span className="font-medium">{stage.label}</span>
                <span className="text-muted-foreground">· {stageStatusLabels[stage.status]}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div
        className={cn(
          'grid items-start gap-4 lg:grid-cols-[200px_minmax(0,1fr)]',
          navCollapsed && 'lg:grid-cols-[56px_minmax(0,1fr)]',
        )}
      >
        <nav
          aria-label="Quote stages"
          className="sticky top-11 hidden gap-0.5 rounded-xl border bg-card p-1.5 lg:grid"
        >
          <button
            aria-label={navCollapsed ? 'Expand quote stages' : 'Collapse quote stages'}
            className={cn(
              'flex h-9 items-center gap-2 rounded-md px-3 text-xs font-medium text-muted-foreground outline-none transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50',
              navCollapsed && 'justify-center px-2',
            )}
            onClick={() => setNavCollapsed((value) => !value)}
            type="button"
          >
            {navCollapsed ? <PanelLeftOpen aria-hidden="true" className="size-4" /> : <PanelLeftClose aria-hidden="true" className="size-4" />}
            {navCollapsed ? null : 'Collapse'}
          </button>
          {workflow.stages.map((stage) => (
            <StageLink
              active={stage.key === activeTab.key}
              collapsed={navCollapsed}
              key={stage.key}
              quoteId={quoteId}
              stage={stage}
            />
          ))}
        </nav>

        <div className="min-w-0">
          {workflow.state === 'started' ? <StartedProject quoteId={quoteId} workflow={workflow} /> : null}
          {workflow.state !== 'started' && activeStage ? <MissingInfo quoteId={quoteId} stage={activeStage} /> : null}
          {workflow.state !== 'started' && activeStage?.status === 'unavailable' ? (
            <UnavailableStage stage={activeStage} />
          ) : null}
          {workflowTabs.map((tab) => {
            if (!visited.includes(tab.key)) return null
            const Tab = tab.component
            // A started project still collects payments: its Start stage stays open below it (USA-1043, USA-1333).
            const afterStart = workflow.state === 'started'
            const active =
              (!afterStart || tab.key === 'invoicing') && activeStage?.status !== 'unavailable' && tab.key === activeTab.key
            return (
              <div
                aria-hidden={!active}
                className={active ? (afterStart ? 'mt-4' : undefined) : 'hidden'}
                data-workflow-stage={tab.key}
                key={tab.key}
              >
                <Tab
                  active={active}
                  onQuoteChange={onQuoteChange}
                  onWorkflowChange={onWorkflowChange}
                  quote={quote}
                  quoteId={quoteId}
                  workflow={workflow}
                />
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
